import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Separator } from './ui/separator';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import SavedReportView from './SavedReportView';
import { Plus, Trash2, Download, FileSpreadsheet, FileText, Eye } from 'lucide-react';

const REPORT_TYPES = [
  ['campaign_performance', 'Campaign performance'],
  ['traffic_quality', 'Traffic quality'],
  ['conversion_funnel', 'Conversion funnel'],
];
const GROUP_BY = ['date', 'campaign', 'traffic_source', 'country', 'device'];
const METRICS = ['clicks', 'conversions', 'revenue', 'roi', 'ctr'];
const STATUSES = ['lead', 'sale', 'reject', 'upsell', 'registration', 'deposit', 'trash'];

const EMPTY_FORM = {
  name: '',
  description: '',
  report_type: 'campaign_performance',
  group_by: ['campaign'],
  metrics: ['clicks', 'conversions', 'revenue'],
  conversion_statuses: [],
};

export default function SavedReports() {
  const { t } = useT();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/reports');
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading reports:', err);
      toast.error(t('Error loading reports'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => setForm(EMPTY_FORM);

  const toggleIn = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((x) => x !== value)
        : [...prev[field], value],
    }));

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    try {
      const res = await axios.post(API + '/reports', {
        name: form.name.trim(),
        description: form.description || null,
        report_type: form.report_type,
        group_by: form.group_by,
        metrics: form.metrics,
        conversion_statuses: form.conversion_statuses,
      });
      setReports((prev) => [res.data, ...prev]);
      setCreateOpen(false);
      resetForm();
      toast.success(t('Report saved'));
    } catch (err) {
      console.error('Error creating report:', err);
      toast.error(t('Error creating report'));
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    try {
      await axios.delete(API + '/reports/' + id);
      setReports((prev) => prev.filter((r) => r.id !== id));
      setDeleteTarget(null);
      toast.success(t('Report deleted'));
    } catch (err) {
      console.error('Error deleting report:', err);
      toast.error(t('Error deleting report'));
    }
  };

  const exportReport = async (report, fmt) => {
    try {
      const res = await axios.get(API + '/reports/' + report.id + '/export-' + fmt, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = (report.name || 'report') + '.' + (fmt === 'xlsx' ? 'xlsx' : 'csv');
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      toast.error(t('Failed to export report'));
    }
  };

  const chip = (field, value, active) => (
    <button
      key={field + ':' + value}
      type="button"
      onClick={() => toggleIn(field, value)}
      className={
        'px-3 py-1 rounded-full border text-sm transition-colors ' +
        (active
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40')
      }
    >
      {value}
    </button>
  );

  const renderForm = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder={t('e.g. Weekly performance')} />
        </div>
        <div className="space-y-2">
          <Label>{t('Report type')}</Label>
          <Select value={form.report_type} onValueChange={(v) => setForm((p) => ({ ...p, report_type: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {REPORT_TYPES.map(([v, l]) => (<SelectItem key={v} value={v}>{t(l)}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>{t('Description')}</Label>
        <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} />
      </div>
      <Separator />
      <div className="space-y-2">
        <Label>{t('Group by')}</Label>
        <div className="flex flex-wrap gap-2">{GROUP_BY.map((g) => chip('group_by', g, form.group_by.includes(g)))}</div>
      </div>
      <div className="space-y-2">
        <Label>{t('Metrics')}</Label>
        <div className="flex flex-wrap gap-2">{METRICS.map((m) => chip('metrics', m, form.metrics.includes(m)))}</div>
      </div>
      <div className="space-y-2">
        <Label>{t('Conversion statuses')}</Label>
        <p className="text-xs text-muted-foreground">{t('Leave empty to count every conversion.')}</p>
        <div className="flex flex-wrap gap-2">{STATUSES.map((s) => chip('conversion_statuses', s, form.conversion_statuses.includes(s)))}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Analytics')}
        title={t('Saved reports')}
        subtitle={t('Reusable report definitions you can open on the screen or export to CSV or XLSX.')}
        actions={
          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}><Plus className="w-4 h-4 mr-2" />{t('New report')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('New saved report')}</DialogTitle>
                <DialogDescription>{t('Define a reusable report and its grouping/metrics.')}</DialogDescription>
              </DialogHeader>
              {renderForm()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('Cancel')}</Button>
                <Button onClick={handleCreate}>{t('Save')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty icon={FileText} title={t('No saved reports yet')} subtitle={t('Create one to export campaign performance on demand.')} />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map((r) => (
            <Card key={r.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-5 h-5 text-primary shrink-0" />
                    <CardTitle className="truncate">{r.name}</CardTitle>
                  </div>
                  <Button variant="ghost" size="sm" aria-label={t('Delete report')} title={t('Delete report')} onClick={() => setDeleteTarget(r)}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <Badge variant="secondary" className="w-fit text-xs">{r.report_type}</Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                {r.description ? <p className="text-sm text-muted-foreground">{r.description}</p> : null}
                {(r.group_by || []).length > 0 ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Group by')}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">{r.group_by.map((g) => (<Badge key={g} variant="outline" className="text-xs">{g}</Badge>))}</div>
                  </div>
                ) : null}
                {(r.metrics || []).length > 0 ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Metrics')}</Label>
                    <div className="flex flex-wrap gap-1 mt-1">{r.metrics.map((m) => (<Badge key={m} variant="secondary" className="text-xs">{m}</Badge>))}</div>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={() => setViewTarget(r)} data-testid="saved-report-open"><Eye className="w-4 h-4 mr-1" />{t('Open report')}</Button>
                  <Button variant="outline" size="sm" onClick={() => exportReport(r, 'csv')}><Download className="w-4 h-4 mr-1" />CSV</Button>
                  <Button variant="outline" size="sm" onClick={() => exportReport(r, 'xlsx')}><FileSpreadsheet className="w-4 h-4 mr-1" />XLSX</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <SavedReportView
        report={viewTarget}
        open={!!viewTarget}
        onOpenChange={(open) => { if (!open) setViewTarget(null); }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete report')}
        description={t('This permanently deletes the saved report definition.')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDelete(deleteTarget && deleteTarget.id)}
      />
    </div>
  );
}
